import React, { useState } from 'react'
import {
  Button,
  cn,
  Label,
  PlainA,
  RadioGroup,
  CheckboxWithLabel,
  Span,
  InputButton,
  DatePicker,
  Select,
  Divider
} from '@pcln/horizon'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { generateTimeSelectOptions } from '@/components/TimeSelect/TimeSelect'
import useBootstrapData from '@/hooks/useBootstrapData'
import { DEFAULT_VALUES, LABELS } from '@/components/Cars/HorizonForm/constants'
import type { PackageFormTripType } from '../types'
import {
  getGridContainerClass,
  horizonPackagesForm
} from './HorizonPackagesForm.variants'
import {
  STAY_FLY,
  STAY_FLY_DRIVE,
  CHECKBOX_TEXT,
  RADIO_OPTIONS
} from './constants'
import { STAY_DRIVE } from '../SearchForm/TripTypes'

const isString = (value: unknown): value is string => {
  return typeof value === 'string'
}

export default function HorizonPackagesForm() {
  const { locale: { localeCode = 'en-US' } = {} } = useBootstrapData()
  const {
    radioButtons,
    originInput,
    destinationInput,
    timelineInput,
    passengersInput,
    advancedSearchSlot,
    checkBoxSlot,
    partStaySlot,
    carPickupSlot
  } = horizonPackagesForm()
  const [packagesType, setPackagesType] =
    useState<PackageFormTripType>(STAY_FLY)
  const [isLabelChecked, setIsLabelChecked] = useState(false)
  const [pickupTime, setPickupTime] = useState<string>(
    DEFAULT_VALUES.PICKUP_TIME
  )
  const [dropoffTime, setDropoffTime] = useState<string>(
    DEFAULT_VALUES.DROPOFF_TIME
  )
  const hasOnlyHotelAndCar = packagesType === STAY_DRIVE
  const hasHotelIncluded = packagesType.includes('STAY')
  const hasHotelAndFlightIncluded =
    packagesType === STAY_FLY || packagesType === STAY_FLY_DRIVE
  const hasHotelPartStayIncluded = isLabelChecked && hasHotelAndFlightIncluded

  return (
    <div className="w-auto">
      <form
        className="w-auto"
        aria-label="horizon-packages-search-form"
        onSubmit={e => e.preventDefault()}
      >
        <div className="w-auto mx-auto">
          <div className={getGridContainerClass(packagesType, isLabelChecked)}>
            {/* Packages Type Radio Buttons */}
            <div className={radioButtons()}>
              <RadioGroup
                className="grid grid-rows-2 sm:grid-rows-1 grid-flow-col auto-cols-min gap-x-10 sm:gap-2"
                name="packagesType"
                defaultValue={STAY_FLY}
                options={RADIO_OPTIONS}
                handleChange={(value: unknown) => {
                  if (isString(value)) {
                    setPackagesType(value)
                  }
                }}
              />
            </div>
            {/* Start Location */}
            {!hasOnlyHotelAndCar && (
              <div className={originInput()}>
                <AutocompleteWrapper
                  searchKey="startLocation"
                  typeAheadEndpoint="hotels"
                />
              </div>
            )}
            {/* End Location */}
            <div className={destinationInput()}>
              <AutocompleteWrapper
                searchKey="endLocation"
                typeAheadEndpoint="hotels"
              />
            </div>

            <div className="contents [grid-area:unset] lg:[grid-area:group] lg:flex items-center gap-2">
              {/* CheckBox */}
              {hasHotelIncluded && (
                <div className={checkBoxSlot()}>
                  <CheckboxWithLabel
                    checked={isLabelChecked}
                    handleChange={() => {
                      setIsLabelChecked(!isLabelChecked)
                    }}
                    labelNode={
                      <Span textStyle="body3">
                        {CHECKBOX_TEXT[packagesType]}
                      </Span>
                    }
                    size="16"
                    name="checkbox-hotel-part-stay"
                    value="hotel"
                  />
                </div>
              )}
              {/* Divider */}
              {hasHotelAndFlightIncluded && (
                <div className="hidden h-4 lg:flex flex-row justify-center">
                  <Divider direction="vertical" />
                </div>
              )}
              {/* Advanced Search */}
              {!hasOnlyHotelAndCar && (
                <div className={advancedSearchSlot()}>
                  <Label textStyle="body3">
                    One-way flights or multiple hotels:
                  </Label>
                  <PlainA className="font-bold" iconRight="arrow_forward">
                    Advanced Search
                  </PlainA>
                </div>
              )}
            </div>
            {/* Hotel Part Stay Checked - Date Picker (check-in/out)  */}
            {hasHotelPartStayIncluded && (
              <div
                data-testid="date-picker-hotel-part-stay"
                className={partStaySlot()}
              >
                <DatePicker
                  label="Check-in - Check-out"
                  mode="range"
                  numberOfMonths={2}
                  handleCalendarChange={() => {
                    console.log('changed')
                  }}
                />
              </div>
            )}
            {/* Date Picker */}
            <div
              data-testid="date-picker-departing-returning"
              className={timelineInput()}
            >
              <DatePicker
                label="Departing - Returning"
                mode="range"
                numberOfMonths={2}
                handleCalendarChange={() => {
                  console.log('changed')
                }}
              />
            </div>
            {/* Travelers */}
            <InputButton
              className={passengersInput()}
              label="Travelers"
              iconLeft="person"
              placeholder="2 Adults, 1 Room"
            />
            {/* Pickup Car Elsewhere */}
            {isLabelChecked && hasOnlyHotelAndCar && (
              <div className={carPickupSlot()}>
                <div className="[grid-area:city]">
                  <AutocompleteWrapper
                    searchKey="cars"
                    typeAheadEndpoint="cars"
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:pickupDate]">
                  <DatePicker
                    label="Pickup"
                    mode="single"
                    numberOfMonths={1}
                    handleCalendarChange={() => {
                      console.log('changed')
                    }}
                  />
                </div>
                <div className="[grid-area:pickupTime]">
                  <Select
                    label={LABELS.PICKUP_TIME}
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={pickupTime}
                    onChange={setPickupTime}
                    placeholder={LABELS.PICKUP_TIME}
                  />
                </div>
                <div className="**:translate-y-0 [grid-area:dropoffDate] ">
                  <DatePicker
                    label="Drop off"
                    mode="single"
                    numberOfMonths={1}
                    handleCalendarChange={() => {
                      console.log('changed')
                    }}
                  />
                </div>
                <div className="[grid-area:dropoffTime]">
                  <Select
                    label={LABELS.DROPOFF_TIME}
                    isInset
                    menuItems={generateTimeSelectOptions(localeCode, true)}
                    initialValue={dropoffTime}
                    onChange={setDropoffTime}
                    placeholder={LABELS.DROPOFF_TIME}
                  />
                </div>
              </div>
            )}
            {/* Search Button */}
            <Button
              type="primary"
              size="lg"
              className={cn('[grid-area:unset] sm:[grid-area:submit]')}
            >
              Search
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
